// PrivacyPolicyModal.js
import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import './PrivacyPolicyModal.css'; // Ensure this CSS file exists and is correctly imported

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const modalVariants = {
  hidden: { y: "-100vh", opacity: 0 },
  visible: { 
    y: "0",
    opacity: 1,
    transition: { delay: 0.2, type: 'spring', stiffness: 100 }
  },
  exit: { y: "-100vh", opacity: 0 }
};

const PrivacyPolicyModal = ({ handleClose }) => {
  const modalRef = useRef(null);

  // Close modal on 'Esc' key press
  useEffect(() => {
    const keyListener = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', keyListener);
    return () => window.removeEventListener('keydown', keyListener);
  }, [handleClose]);

  // Focus on modal when it opens
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  return (
    <motion.div
      className="backdrop"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      onClick={handleClose}
      aria-modal="true"
      role="dialog"
    >
      <motion.div
        className="modal"
        variants={modalVariants}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
        tabIndex="-1"
        ref={modalRef}
      >
        <button 
          className="close-button" 
          onClick={handleClose} 
          aria-label="Close Privacy Policy"
        >
          &times;
        </button>
        <h2>Privacy Policy for SeeOSC</h2>
        <p><strong>Effective Date:</strong> January 16, 2025</p>
        
        <section>
          <h3>1. Introduction</h3>
          <p>
            Welcome to SeeOSC ("we," "our," or "us"). We respect your privacy and are committed to protecting any information you share with us. This Privacy Policy outlines how SeeOSC handles your information when you use our mobile application SeeOSC ("App").
          </p>
          <p>
            By accessing or using our App, you agree to the terms of this Privacy Policy. If you do not agree with our practices, please do not use the App.
          </p>
        </section>
        
        <section>
          <h3>2. Information We Do Not Collect</h3>
          <p>
            SeeOSC is designed with your privacy in mind. We want to assure you that:
          </p>
          <ul>
            <li><strong>No Personal Data Collection:</strong> We do not collect, store, or process any personal data from you. This includes, but is not limited to, your name, email address, location, or any other identifying information.</li>
            <li><strong>No Sensor Data Collection:</strong> We do not collect, store, or process any sensor data from you, including biometric data.</li>
            <li><strong>No Usage Tracking:</strong> We do not track your usage of the App, including interactions, preferences, or device information.</li>
            <li><strong>No Third-Party Analytics:</strong> We do not integrate with any third-party analytics or advertising services that collect data about your usage.</li>
          </ul>
        </section>
        
        <section>
          <h3>3. Data Sharing</h3>
          <p>
            SeeOSC only shares data based on your explicit instructions and operates solely within your local network. Specifically:
          </p>
          <h4>Local Network Sharing:</h4>
          <ul>
            <li><strong>User-Initiated:</strong> Data transmission occurs only when you choose to share data via the App. This sharing is facilitated through OSC (Open Sound Control) protocols to devices or servers within your local network.</li>
            <li><strong>No External Sharing:</strong> We do not transmit your data to external servers, cloud services, or third parties unless explicitly directed by you within the App.</li>
          </ul>
          <h4>No Data Retention:</h4>
          <ul>
            <li><strong>Temporary Transmission:</strong> All data shared via OSC is not stored on our servers or any other storage medium. Data is transmitted in real-time and only exists during the active sharing session.</li>
          </ul>
        </section>
        
        <section>
          <h3>4. Your Rights and Choices</h3>
          <p>
            Since SeeOSC does not collect any personal data, you have full control over your data sharing preferences:
          </p>
          <h4>Opt-In Sharing:</h4>
          <ul>
            <li><strong>Voluntary:</strong> Data sharing occurs only when you choose to initiate it within the App. You can start or stop sharing at any time without any restrictions.</li>
          </ul>
          <h4>No Data Access Requests:</h4>
          <ul>
            <li><strong>Not Applicable:</strong> As we do not store or process your data, there are no data access, correction, or deletion requests applicable.</li>
          </ul>
        </section>
        
        <section>
          <h3>5. Children's Privacy</h3>
          <p>
            SeeOSC is not intended for use by individuals under the age of 13. We do not knowingly collect any information from children. If you believe a child has provided us with information, please contact us, and we will promptly delete such information.
          </p>
        </section>
        
        <section>
          <h3>6. Changes to This Privacy Policy</h3>
          <p>
            We reserve the right to modify this Privacy Policy at any time. Any changes will be effective immediately upon posting the updated policy within the App. We encourage you to review this Privacy Policy periodically to stay informed about our data practices.
          </p>
        </section>
        
        <section>
          <h3>7. Contact Us</h3>
          <p>
            If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
          </p>
          <p>
            <strong>Email:</strong> <a href="mailto:info@seeosc.com">info@seeosc.com</a><br />
          </p>
          <p><em>Last Updated: January 16, 2025</em></p>
        </section>
      </motion.div>
    </motion.div>
  );
};

export default PrivacyPolicyModal;
